import {useState} from 'react';
import {usePolling} from '../api';
import {Badge, Stage, fmtTime, jsonShort} from '../components';

const STATUSES = ['active', 'pending', 'passed', 'rejected', 'flagged',
  'unresolved', 'quarantine', 'done', 'needs_human'];
const STAGES = ['S1_symmetry', 'S2_alignn_gap', 'S3_vasp_relax',
  'S4_vasp_magnetic', 'S5_mp_hull', 'S6_prophet_phonons', 'S7_qe_relax_pm',
  'S8_qe_phonons', 'S9_epw_tc'];

function DetailDrawer({id, onClose}) {
  const {data} = usePolling(`/candidates/${id}`, null, 10000);
  return (
    <>
      <div className="drawer-mask" onClick={onClose} />
      <div className="drawer">
        <button className="close" onClick={onClose}>×</button>
        {!data ? <p className="muted">加载中…</p> : (
          <>
            <h2 style={{fontSize: 16}}>
              #{data.ordinal} {data.formula || data.material_id} <Badge v={data.status} />
            </h2>
            <dl className="kv">
              <dt>run</dt><dd>#{data.run}</dd>
              <dt>material_id</dt><dd className="mono">{data.material_id || '—'}</dd>
              <dt>priority_rank</dt><dd>{data.priority_rank}</dd>
              <dt>natoms</dt><dd>{data.natoms ?? '—'}</dd>
              <dt>input_sha256</dt><dd className="mono">{data.input_sha256.slice(0, 16) || '—'}…</dd>
              <dt>current_stage</dt><dd><Stage s={data.current_stage} /></dd>
              <dt>terminal_label</dt><dd>{data.terminal_label ? <Badge v={data.terminal_label} /> : '—'}</dd>
              <dt>updated_at</dt><dd>{fmtTime(data.updated_at)}</dd>
            </dl>

            {(() => {
              const s9 = (data.stage_results || []).find(
                r => r.stage === 'S9_epw_tc' && r.metrics?.tc);
              const tc = s9?.metrics.tc;
              if (!tc || tc.tc_primary_K == null) return null;
              const f2 = v => v == null ? '—' : Number(v).toFixed(2);
              return (
                <div className="panel" style={{margin: '14px 0', padding: 12}}>
                  <h3 style={{margin: '0 0 8px'}}>超导指标（EPW，μ*={tc.mu_star}）</h3>
                  <div className="stat-cards">
                    <div className="stat-card">
                      <div className="num" style={{fontSize: 24}}>
                        {Number(tc.tc_primary_K).toFixed(2)} K
                      </div>
                      <div className="lbl">Tc（主判据）</div>
                    </div>
                    <div className="stat-card">
                      <div className="num" style={{fontSize: 24}}>{f2(tc.lambda_epw)}</div>
                      <div className="lbl">λ 电声耦合</div>
                    </div>
                    <div className="stat-card">
                      <div className="num" style={{fontSize: 24}}>
                        {Math.round(tc.omega_log_mev)} meV
                      </div>
                      <div className="lbl">ω_log 对数平均声子频率</div>
                    </div>
                    <div className="stat-card">
                      <div className="num" style={{fontSize: 24}}>{f2(tc.tc_mcmillan_epw_K)} K</div>
                      <div className="lbl">Tc（McMillan）</div>
                    </div>
                  </div>
                  <p className="muted" style={{margin: '8px 0 0', fontSize: 12}}>
                    {tc.tc_primary_K === tc.tc_allen_dynes_K
                      ? '主判据 = Allen-Dynes 公式'
                      : `另：Allen-Dynes ${f2(tc.tc_allen_dynes_K)} K`} ·
                    Wannier {tc.wannier_converged ? '收敛' : '未收敛'}
                    {tc.wannier_num_wan ? `（${tc.wannier_num_wan} 个轨道）` : ''} ·
                    点击芯片旁数值可查原始 JSON
                  </p>
                </div>);
            })()}

            <h3 style={{margin: '16px 0 8px'}}>科学指标</h3>
            {data.metrics && Object.keys(data.metrics).length
              ? <dl className="kv">{Object.entries(data.metrics).map(([k, v]) => (
                  <div key={k} style={{display: 'contents'}}>
                    <dt>{k}</dt><dd className="mono">{jsonShort(v)}</dd>
                  </div>))}</dl>
              : <p className="muted">暂无</p>}

            <h3 style={{margin: '16px 0 8px'}}>阶段时间线</h3>
            <table>
              <thead><tr><th>stage</th><th>status</th><th>label</th><th>metrics</th></tr></thead>
              <tbody>
                {(data.stage_results || []).map(r => (
                  <tr key={r.id} style={{cursor: 'default'}}>
                    <td><Stage s={r.stage} /></td>
                    <td><Badge v={r.status} /></td>
                    <td className="mono">{r.label || '—'}</td>
                    <td className="muted mono">{jsonShort(r.metrics, 60)}</td>
                  </tr>))}
              </tbody>
            </table>

            <h3 style={{margin: '16px 0 8px'}}>stage_runs（含 fan-out/chunk）</h3>
            <table>
              <thead><tr><th>stage</th><th>scope</th><th>att</th><th>status</th><th>时间</th></tr></thead>
              <tbody>
                {(data.stage_runs || []).map(r => (
                  <tr key={'sr' + r.id} style={{cursor: 'default'}}>
                    <td><Stage s={r.stage} /></td>
                    <td className="mono">{r.scope || '—'}</td>
                    <td>{r.attempt}</td>
                    <td><Badge v={r.status} /></td>
                    <td className="muted mono">{fmtTime(r.started_at)} → {fmtTime(r.finished_at)}</td>
                  </tr>))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </>
  );
}

export default function Candidates() {
  const [filters, setFilters] = useState({status: '', current_stage: '', search: ''});
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState(null);
  const limit = 50;
  const {data, error, loading} = usePolling('/candidates', {...filters, limit, offset});

  const setF = (k, v) => { setFilters(f => ({...f, [k]: v})); setOffset(0); };

  return (
    <div>
      <h2>Candidates</h2>
      <div className="filters">
        <input placeholder="搜索 formula / material_id" value={filters.search}
               onChange={e => setF('search', e.target.value)} style={{width: 220}} />
        <select value={filters.status} onChange={e => setF('status', e.target.value)}>
          <option value="">全部状态</option>
          {STATUSES.map(s => <option key={s}>{s}</option>)}
        </select>
        <select value={filters.current_stage} onChange={e => setF('current_stage', e.target.value)}>
          <option value="">全部阶段</option>
          {STAGES.map(s => <option key={s}>{s}</option>)}
        </select>
        {loading && <span className="muted">刷新中…</span>}
      </div>

      {error && <p className="muted">API 错误：{error}</p>}
      <div className="panel" style={{padding: 0, overflowX: 'auto'}}>
        <table>
          <thead>
            <tr>
              <th>#</th><th>pri</th><th>formula</th><th>natoms</th><th>status</th>
              <th>stage</th><th>label</th><th>updated</th>
            </tr>
          </thead>
          <tbody>
            {(data || []).map(c => (
              <tr key={c.id} onClick={() => setSelected(c.id)}>
                <td className="mono">{c.ordinal}</td>
                <td>{c.priority_rank}</td>
                <td>{c.formula || <span className="muted">{c.material_id}</span>}</td>
                <td>{c.natoms ?? '—'}</td>
                <td><Badge v={c.status} /></td>
                <td><Stage s={c.current_stage} /></td>
                <td className="mono">{c.terminal_label || '—'}</td>
                <td className="muted mono">{fmtTime(c.updated_at)}</td>
              </tr>))}
            {data && data.length === 0 && (
              <tr><td colSpan={8} className="muted">无数据（尚未创建 run 或过滤过严）</td></tr>)}
          </tbody>
        </table>
      </div>
      <div style={{display: 'flex', gap: 8, alignItems: 'center'}}>
        <button className="btn" disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - limit))}>上一页</button>
        <button className="btn" disabled={!data || data.length < limit}
                onClick={() => setOffset(offset + limit)}>下一页</button>
        <span className="muted">offset {offset}</span>
      </div>
      {selected && <DetailDrawer id={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
